/* Native FE-compute model + recompute, extracted from classificationStore so the bounded on-device
   evaluation of computed fields/columns can be unit-tested without standing up the whole Vuex store.
   The store imports buildNativeModel/nativeRecompute from here and owns the state these read/write. */

import _ from "lodash";
import Vue from "vue";
import { DateTime } from "luxon";
import FETIASEntity from "../../business/FETIASEntity";
import nativeFormula from "../../business/nativeFormula";
import commons from "../commons/commons";

/* The clock injected into the native evaluator for today()/now(). Computed in the browser's local
   timezone -- the same zone the client advertises to the server in the `tz` request header -- so a
   value shown here equals the server's authoritative submit-time recompute (which resolves today/now
   in that same zone). ISO-8601 strings match how date fields are already stored on submit
   (luxon .toISO()), so the value does not churn when the server re-asserts it. */
function nativeClock() {
  const now = DateTime.now();
  return {
    today: now.startOf("day").toISO(), // e.g. "2026-06-23T00:00:00.000+08:00"
    now: now.toISO()                    // e.g. "2026-06-23T14:30:45.123+08:00"
  };
}

/* Topologically order client-computable fields so a field that depends on another computed field
   in the same scope is evaluated after it. */
function orderComputable(fields) {
  const byName = {};
  fields.forEach(f => { byName[f.displayName] = f; });
  const order = [], visited = {}, visiting = {};
  const visit = (f) => {
    if (visited[f.displayName] || visiting[f.displayName]) return; // done or cycle guard
    visiting[f.displayName] = true;
    (f.dependsOn || []).forEach(dn => { if (byName[dn]) visit(byName[dn]); });
    visiting[f.displayName] = false;
    visited[f.displayName] = true;
    order.push(f);
  };
  fields.forEach(visit);
  return order;
}

/* Coerce a control-formula result to a boolean EXACTLY as the server's AutoFallback does
   (FormulaProcessor.cs:412) — NOT JS truthiness: bool passes through; null/undefined -> false;
   string -> length > 0 (so "" -> false); NUMBERS -> false; objects/arrays (the server's JObject
   case) -> false. Parity here is what makes the on-device preview match the server's synced
   answer for the same expression. */
export function autoFallbackTruthy(value) {
  if (value === true || value === false) return value;
  if (value == null) return false;
  if (typeof value === "string") return value.length > 0;
  return false;
}

/* Apply one form-change payload (the sync response / native-control shape) to a design element
   through Vue.set so the form re-renders. Single authority shared by the store's three sync
   application sites and the native control apply below — local application MUST go through the
   exact same setters as the server's synced answers. */
export function applyElementFormChange(element, change) {
  if (!element || !change) return;
  if (change.visible === true || change.visible === false) {
    Vue.set(element, "visible", change.visible);
  }
  if (change.required === true || change.required === false) {
    Vue.set(element, "required", change.required);
  }
  if (change.readOnly === true || change.readOnly === false) {
    Vue.set(element, "readOnly", change.readOnly);
  }
  if (change.input != null) {
    Vue.set(element, "input", change.input);
  }
  if (change.optionList != null) {
    Vue.set(element, "optionList", change.optionList);
  }
}

/* Build the ordered native-compute model from a NativeFormulaBundle: top-level value fields plus
   per-row computed columns (grouped by their table), and — when the bundle's ECHOED version is
   >= 4 (the D-7 handshake: min(client, server), never our own advertised number) — the per-field
   client-computable control formulas and the FullyClientOwned trigger-suppression set.
   Returns null when there is nothing to compute. */
export function buildNativeModel(bundle) {
  if (!bundle || !Array.isArray(bundle.fields)) return null;

  const topLevel = bundle.fields.filter(f => f.clientComputable && f.formula && f.fieldName);

  const columnTables = bundle.fields
    .filter(f => f.fieldName && Array.isArray(f.columns) && f.columns.length)
    .map(f => ({
      tableFieldName: f.fieldName,
      tableDisplayName: f.displayName,
      columns: orderComputable(f.columns.filter(c => c.clientComputable && c.formula && c.fieldName))
    }))
    .filter(t => t.columns.length);

  const v4 = (bundle.version || 0) >= 4;

  /* Field-level control formulas (visible/readOnly/required). A ≤3 bundle never carries these. */
  const controlFields = v4
    ? bundle.fields
      .filter(f => f.id && Array.isArray(f.controls) && f.controls.length)
      .map(f => ({
        id: f.id,
        displayName: f.displayName,
        controls: f.controls
          .filter(c => c && c.formula && (c.type === "visible" || c.type === "readOnly" || c.type === "required"))
          /* failed: D-3 — an on-device eval failure flips this control back to server-owned
             for the session (no local apply, no trigger suppression for its field). */
          .map(c => ({ type: c.type, formula: c.formula, failed: false }))
      }))
      .filter(f => f.controls.length)
    : [];

  /* Trigger-suppression set: display names of fields the server declared FULLY client-owned
     (value + every control shipped; for tables, every column too). Server-computed — the client
     cannot infer it because it never sees the server-only formulas. */
  const owned = v4
    ? new Set(bundle.fields.filter(f => f.fullyClientOwned && f.displayName).map(f => f.displayName))
    : null;

  if (!topLevel.length && !columnTables.length && !controlFields.length) return null;

  return {
    version: bundle.version || 0,
    order: orderComputable(topLevel),
    columnTables,
    controlFields,
    owned,
    fullyClientComputable: !!bundle.fullyClientComputable
  };
}

/* Identify the form field a legacy front-trigger target writes to. A target is "{ primary, secondary }":
   secondary set  -> a table column (primary = table display name, secondary = column display name);
   secondary null -> a top-level field (primary = field display name). True when that field is one the
   native recompute already owns.

   v4 (model.owned present): a FIELD-level target strips only when the server declared the field
   FullyClientOwned — value AND every control formula shipped client-computable. This is strictly
   safer than the value-only check: a field with a client value formula but a server control
   formula keeps syncing (proposal §2.3). Column targets keep the value-computed check (column
   controls are v4.1, D-4). A ≤3 bundle has no ownership data -> legacy value-only behavior. */
function targetIsClientComputed(model, target) {
  if (!model || !target) return false;
  if (target.secondary) {
    const table = (model.columnTables || []).find(t => t.tableDisplayName === target.primary);
    return !!(table && table.columns.some(c => c.displayName === target.secondary));
  }
  if (model.owned) return model.owned.has(target.primary);
  // f.failed: the on-device eval gave up on this field (D-3), so its triggers must survive
  return (model.order || []).some(f => f.displayName === target.primary && !f.failed);
}

/* Remove every trigger target the native recompute owns (a client-computable computed field/column),
   dropping a trigger left with no targets. Used for BOTH the legacy front-trigger (eval) list and the
   backend-sync trigger list -- they share the same { primary (input), secondary, targets:[...] } shape.
   Computed fields are always evaluated natively at min(server, FE) version, so they are skipped on the
   eval path AND must not, on their own, force a /formsync round-trip -- nativeRecompute computes them
   and the server recomputes authoritatively on submit. A trigger whose only targets are native is
   dropped entirely; one with a mix keeps its non-native targets, so server formulas / backend
   automation still sync as before. Returns the input unchanged when there is no native model.

   Example (the table case that motivated the backend-trigger split):
     { primary:"Details", secondary:null,      targets:[No, Subtotal] }   -> both native  -> dropped
     { primary:"Details", secondary:"Product", targets:[Description]   }   -> server field -> kept */
export function stripNativeTriggers(triggers, model) {
  if (!Array.isArray(triggers) || !model) return triggers;
  const kept = [];
  triggers.forEach(trigger => {
    const targets = trigger.targets || [];
    const remaining = targets.filter(t => !targetIsClientComputed(model, t));
    if (remaining.length === 0) return; // whole trigger owned by native compute -> drop it
    kept.push(remaining.length === targets.length ? trigger : { ...trigger, targets: remaining });
  });
  return kept;
}

/* Find a table/list element in the form by fieldName, returning its column schema (the `fields`
   array) so per-row entities can resolve colField('Display Name') -> row value by display name. */
function findColumnSchema(form, fieldName) {
  let found = null;
  const walkRows = (rows) => {
    if (!Array.isArray(rows)) return;
    rows.forEach(row => {
      (row.items || []).forEach(item => walkItem(item));
    });
  };
  const walkItem = (item) => {
    if (!item || found) return;
    if (item.fieldName === fieldName && Array.isArray(item.fields)) { found = item.fields; return; }
    if (item.design) walkRows(item.design.rows);
    if (Array.isArray(item.tabs)) item.tabs.forEach(t => t.design && walkRows(t.design.rows));
  };
  walkRows(form && form.rows);
  return found;
}

/* Evaluate every client-computable field (top-level and per-row columns) locally against the
   current form data and write the results back. Mirrors the value-write + clone-for-reactivity
   pattern the front-trigger path uses. Returns true when at least one value changed. */
export function nativeRecompute(context) {
  const model = context.state.nativeModel;
  if (!model || !context.state.formData) return false;

  const inputFields = commons.getAllElements(null, context.state.form);
  const entity = new FETIASEntity(context.state.formData, inputFields);

  /* resolver for table aggregates (sumColumn/sumColCol/sumColConst): given a table's display name,
     return its rows as entities so field('Column') resolves per row by display name. */
  const rows = (tableDisplayName) => {
    const tableField = inputFields.find(f => f && f.displayName === tableDisplayName);
    if (!tableField) return [];
    const rowsJson = context.state.formData[tableField.fieldName];
    if (!Array.isArray(rowsJson)) return [];
    const colSchema = findColumnSchema(context.state.form, tableField.fieldName)
      || (Array.isArray(tableField.fields) ? tableField.fields : []);
    return rowsJson.map(rj => new FETIASEntity(rj, colSchema));
  };

  let changed = false;

  // one clock for the whole pass, so every today()/now() in this form is internally consistent.
  const clock = nativeClock();
  const clockOpts = { today: clock.today, now: clock.now };

  // top-level computed fields
  model.order.forEach(f => {
    if (f.failed) return;
    const res = nativeFormula.evaluate(f.formula, entity, null, { rows, ...clockOpts });
    if (!res.ok) {
      /* D-3 fail-open, value side: a field that cannot be computed on-device must not keep its
         sync triggers stripped — otherwise it silently holds a stale value forever. Hand it back
         to the server for the session, same as a failed control. */
      f.failed = true;
      if (model.owned) model.owned.delete(f.displayName);
      model.fullyClientComputable = false;
      return;
    }
    if (context.state.formData[f.fieldName] !== res.value) {
      context.state.formData[f.fieldName] = res.value;
      changed = true;
    }
  });

  // per-row computed columns (Subtotal = Unit Price × Quantity, evaluated for each row)
  (model.columnTables || []).forEach(table => {
    const rows = context.state.formData[table.tableFieldName];
    if (!Array.isArray(rows)) return;
    const colSchema = findColumnSchema(context.state.form, table.tableFieldName) || [];
    rows.forEach((rowJson, rowIndex) => {
      const rowEntity = new FETIASEntity(rowJson, colSchema);
      table.columns.forEach(c => {
        // rowIndex + 1 feeds rowNumber() (rowAutoNumber columns); matches the server's 1-based
        // formulaRowNumber and the legacy front-trigger numbering.
        const res = nativeFormula.evaluate(c.formula, entity, rowEntity, { rowNumber: rowIndex + 1, ...clockOpts });
        if (res.ok && rowJson[c.fieldName] !== res.value) {
          rowJson[c.fieldName] = res.value;
          changed = true;
        }
      });
    });
  });

  /* v4 control formulas (visible/readOnly/required), evaluated AFTER values so a control that
     depends on a computed value sees the fresh result. Results apply through the same element
     setters the sync path uses (applyElementFormChange), coerced with server-AutoFallback parity.
     The server still recomputes control state on every sync/submit it does see (D-1); this is
     the on-device preview that makes the round trip unnecessary for owned fields. */
  (model.controlFields || []).forEach(f => {
    let element = null; // resolved lazily; a field not on this design variant is skipped
    f.controls.forEach(c => {
      if (c.failed) return;
      /* strictEntryScope: a control's server answer is already on the element and is correct
         (resolved against the saved entry). Reading Entry off form data here would override it
         with a different value and keep it until the next sync — SS05941. */
      const res = nativeFormula.evaluate(
        c.formula, entity, null, { rows, strictEntryScope: true, ...clockOpts });
      if (!res.ok) {
        /* D-3 fail-open: this control goes back to server-owned for the session — stop
           evaluating it, stop suppressing its field's sync triggers, and drop the zero-sync
           fast path so the server's answer comes back and wins. */
        c.failed = true;
        if (model.owned) model.owned.delete(f.displayName);
        model.fullyClientComputable = false;
        return;
      }
      if (element === null) {
        element = commons.findElementById(
          (context.state.form && context.state.form.rows) || [], f.id, null, null) || false;
      }
      if (!element) return;
      const value = autoFallbackTruthy(res.value);
      if (element[c.type] !== value) {
        applyElementFormChange(element, { [c.type]: value });
      }
    });
  });

  if (changed) {
    /* re-clone so Vue picks up the mutated values (same approach as front-triggers) */
    context.state.formData = _.cloneDeep(context.state.formData);
  }
  return changed;
}
